/**
 * @file usePickingTaskScreen.ts
 * @description Склейка экрана задания на сборку: данные, поиск, фокус поля
 * скана и открытый диалог (phase19.24).
 *
 * Данные живут в `usePickingTask`, фильтр — в `usePickingSearch`, но экрану
 * нужно третье: правило «после любой операции и закрытия диалога фокус
 * возвращается в поле скана» и общий `scanRef`, который знают и панель
 * отметки, и список. Держать это в странице значило снова разогнать её за
 * 200 строк — правило `code-style` про размер компонентов.
 */

import { useEffect, useRef, useState } from "react";

import type { PickingLockReason } from "./picking-item-texts";
import type { PickingDialogState } from "./PickingTaskDialogs";
import { usePickingSearch } from "./usePickingSearch";
import { usePickingTask } from "./usePickingTask";
import type { ScanFieldHandle } from "../../ui/ScanField";
import { isActivePickingTask } from "../../../lib/api-picking";

interface Access {
  /** Право SHOP_WMS_PICKING. */
  canExecute: boolean;
  /** Текущий пользователь: своё задание можно отмечать. */
  userId: string | null;
}

export function usePickingTaskScreen(
  taskId: string | undefined,
  { canExecute, userId }: Access,
) {
  const pick = usePickingTask(taskId);
  const task = pick.task;
  const active = task ? isActivePickingTask(task.status) : false;
  const mine = task?.assignee?.id === userId;
  // Отмечать можно, если задание живо и либо ничьё, либо своё (сервер так же).
  const editable = active && canExecute && (!task?.assignee || mine);
  // Почему нельзя — считаем здесь, рядом с `editable`, иначе панель скана
  // повторяет ту же логику и рано или поздно расходится с ней.
  // Порядок ответов: сначала состояние документа (закрыт — и разговор
  // окончен), потом собственные права (их не поправить «отпустив» задание),
  // потом занятость другим сотрудником.
  let lockReason: PickingLockReason | null = null;
  if (editable || !task) {
    lockReason = null;
  } else if (!active) {
    lockReason = "closed";
  } else if (!canExecute) {
    lockReason = "noPermission";
  } else {
    lockReason = "foreign";
  }
  const search = usePickingSearch(task?.items, editable);
  const scanRef = useRef<ScanFieldHandle>(null);
  const [dialog, setDialog] = useState<PickingDialogState>({ kind: "none" });

  // Автоскролл к отсканированной строке: кладовщик не ищет её глазами.
  useEffect(() => {
    if (!pick.scannedItemId) return;
    document
      .getElementById(`pick-item-${pick.scannedItemId}`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [pick.scannedItemId]);

  /** Фокус в поле скана: после ±, отметки и закрытия любого диалога. */
  function focusScan() {
    scanRef.current?.focus();
  }

  return {
    pick,
    search,
    active,
    mine,
    editable,
    /** Причина запрета отметки — подпись под полем скана. `null` = можно. */
    lockReason,
    /** Задание ничьё и его можно взять в работу. */
    canClaim: canExecute && active && !task?.assignee,
    scanRef,
    dialog,
    setDialog,
    focusScan,
    closeDialog: () => {
      pick.clearActionError();
      setDialog({ kind: "none" });
      focusScan();
    },
    resetFilter: () => {
      search.reset();
      scanRef.current?.clear();
    },
    /** Ввод завершён: скан уходит на сервер, обычный текст только фильтрует. */
    submitScan: (code: string, fromScanner: boolean) => {
      const toServer = search.routeSubmit(code, fromScanner);
      if (!toServer) return;
      scanRef.current?.clear();
      void pick.scan(toServer);
    },
    /** Отметить строку итоговым количеством и вернуть фокус в поле скана. */
    setQty: (itemId: string, qty: number) => {
      void pick.setQty(itemId, qty);
      focusScan();
    },
    /** Снять отметку недостачи со строки — фокус так же уходит в сканер. */
    clearShortage: (itemId: string) => {
      void pick.clearShortage(itemId);
      focusScan();
    },
  };
}
